/**
 * Verified identity (`account-security`):
 * the settings offer, the "Identity verified" line, the required-for-privileged
 * banner and block, the enrollment result, the approver's name-match advisory
 * and the staff badge. Pure functions over the server's fields
 * (docs/backend/account-security-api.md, "Verified identity"); with the flags
 * off none of those fields exist and every function here answers "nothing".
 */

type Rec = Record<string, unknown>;

export interface IdentityEnrollmentInfo {
  status: 'pending' | 'passed' | 'failed' | 'expired' | 'cancelled' | string;
  reason: string | null;
  /** `legal_name_change`: the ID's name differs from the verified legal name. */
  next_step: string | null;
  started_at?: string | null;
  decided_at?: string | null;
}

/** `GET /account-security/status` → `identity` (all keys present when the flag is on). */
export interface IdentityStatusBlock {
  verified: boolean;
  verified_at: string | null;
  /** Legal name with the middle name as an initial ("Robert J. Smith"). */
  display_name: string | null;
  offer: boolean;
  enrollment: IdentityEnrollmentInfo | null;
  required: boolean;
  identity_verification_required: boolean;
  grace_ends_at: string | null;
  blocked: boolean;
}

/** `verified_identity` on `GET /users/:id`, for staff with users.update. */
export interface VerifiedIdentitySummary {
  verified: boolean;
  verified_at: string | null;
  display_name: string | null;
  legal_name?: { first: string | null; middle: string | null; last: string | null } | null;
  verified_via?: string | null;
}

/** `name_match` on a recovery request, `null` until its ID check passed. */
export interface RecoveryNameMatch {
  result: 'match' | 'mismatch' | string;
  id_name?: string | null;
  compared_name?: string | null;
  compared_with?: 'account_name' | 'verified_identity' | string | null;
}

/** The status block, or null (flag off / malformed). */
export function identityBlock(status: unknown): IdentityStatusBlock | null {
  const raw = status && typeof status === 'object' ? (status as Rec)['identity'] : null;
  return raw && typeof raw === 'object' ? (raw as IdentityStatusBlock) : null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 2026" from an ISO time, in UTC; '' when unreadable. */
export function verifiedMonth(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!isFinite(t)) return '';
  const d = new Date(t);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "Identity verified (Robert J. Smith, Oct 2026)", or null when not verified. */
export function verifiedIdentityLabel(v: Pick<VerifiedIdentitySummary, 'verified' | 'verified_at' | 'display_name'> | null | undefined): string | null {
  if (!v || !v.verified) return null;
  const parts = [v.display_name || '', verifiedMonth(v.verified_at)].filter(Boolean);
  return parts.length ? `Identity verified (${parts.join(', ')})` : 'Identity verified';
}

/** The localStorage key for "Maybe later", per user. */
export function identityOfferDismissKey(userId: number | string): string {
  return `dealr.identity-offer-dismissed.${userId}`;
}

/**
 * Show the optional offer: the server allows it, not yet verified, not
 * required (the banner speaks then), no check running, and not dismissed.
 */
export function showIdentityOffer(block: IdentityStatusBlock | null, dismissed: boolean): boolean {
  if (!block || !block.offer || block.verified || block.identity_verification_required) return false;
  if (block.enrollment && block.enrollment.status === 'pending') return false;
  return !dismissed;
}

export type IdentityRequirementState = 'none' | 'grace' | 'blocked';

export interface IdentityRequirementView {
  state: IdentityRequirementState;
  /** Whole days left in the grace period (0 on the last day); null outside grace. */
  daysLeft: number | null;
  title: string;
  message: string;
}

const NONE: IdentityRequirementView = { state: 'none', daysLeft: null, title: '', message: '' };

/**
 * The required-for-privileged state from the status block or a login body
 * (`identity_verification_required`, `identity_grace_ends_at`,
 * `identity_verification_blocked`).
 */
export function identityRequirement(source: unknown, now: number): IdentityRequirementView {
  if (!source || typeof source !== 'object') return NONE;
  const s = source as Rec;
  const block = identityBlock(s);
  const required = block ? block.identity_verification_required === true : s['identity_verification_required'] === true;
  if (!required) return NONE;
  const blocked = block ? block.blocked === true : s['identity_verification_blocked'] === true;
  const graceRaw = block ? block.grace_ends_at : (s['identity_grace_ends_at'] as string | null | undefined);
  if (blocked) {
    return {
      state: 'blocked',
      daysLeft: null,
      title: 'Verify your identity to keep using Dealr',
      message: 'Your role can change users and permissions, so your dealership requires a verified identity. It takes about two minutes with your ID and phone. If you’d rather not use an ID check, another admin at your dealership can confirm who you are from the Users page.',
    };
  }
  const ends = graceRaw ? Date.parse(graceRaw) : NaN;
  const daysLeft = isFinite(ends) ? Math.max(0, Math.floor((ends - now) / 86400000)) : null;
  const when = daysLeft === null ? 'soon' : daysLeft === 0 ? 'today' : daysLeft === 1 ? 'within 1 day' : `within ${daysLeft} days`;
  return {
    state: 'grace',
    daysLeft,
    title: `Verify your identity ${when}`,
    message: 'Your role can change users and permissions, so your dealership requires a verified identity. It takes about two minutes with your ID and phone. After that, you can keep working as usual. If you’d rather not use an ID check, another admin at your dealership can confirm who you are from the Users page.',
  };
}

/** A 403 `identity_verification_required`: the shell sends the person to verify. */
export function identityGateError(status: number, data: unknown): boolean {
  return status === 403 && !!data && typeof data === 'object' && (data as Rec)['code'] === 'identity_verification_required';
}

const ENROLLMENT_REASONS: Record<string, string> = {
  id_mismatch: 'The ID didn’t match your account.',
  document_attempts: 'Your ID couldn’t be read after several tries.',
  liveness_attempts: 'The face check couldn’t be completed after several tries.',
  declined: 'You stopped at the consent step.',
  link_expired: 'The link expired before the check was finished.',
  session_expired: 'The check timed out before it was finished.',
  superseded: 'A newer check replaced this one.',
};

/** The latest enrollment check as one line for the settings card, or null when there is nothing to say. */
export function enrollmentResultMessage(enrollment: IdentityEnrollmentInfo | null | undefined): { tone: 'info' | 'danger'; message: string; nameChange: boolean } | null {
  if (!enrollment) return null;
  if (enrollment.next_step === 'legal_name_change') {
    return {
      tone: 'danger',
      nameChange: true,
      message: 'The name on your ID is different from your verified legal name. If your legal name changed, request a legal name change; your dealership approves it.',
    };
  }
  if (enrollment.status === 'pending') return { tone: 'info', nameChange: false, message: 'Your identity check is in progress. Finish it on your phone; this page updates when it’s done.' };
  if (enrollment.status === 'failed' || enrollment.status === 'expired' || enrollment.status === 'cancelled') {
    const why = (enrollment.reason && ENROLLMENT_REASONS[enrollment.reason]) || 'The last check didn’t finish.';
    return { tone: 'danger', nameChange: false, message: `${why} You can start a new check.` };
  }
  return null;
}

export interface NameMatchView {
  result: 'match' | 'mismatch';
  label: string;
  tone: 'success' | 'warning';
  /** Both names, only when the server sent them (authorized approvers). */
  idName: string | null;
  comparedName: string | null;
  comparedWith: string;
}

/** The approver's name-match advisory, or null before the ID check passed. */
export function nameMatchView(match: RecoveryNameMatch | null | undefined): NameMatchView | null {
  if (!match || (match.result !== 'match' && match.result !== 'mismatch')) return null;
  const mismatch = match.result === 'mismatch';
  const idName = match.id_name && match.compared_name ? match.id_name : null;
  return {
    result: match.result,
    label: mismatch ? 'Name on ID differs (advisory)' : 'Name on ID matches',
    tone: mismatch ? 'warning' : 'success',
    idName,
    comparedName: idName ? (match.compared_name as string) : null,
    comparedWith: match.compared_with === 'verified_identity' ? 'verified legal name' : 'account name',
  };
}
